import { MaskedInput } from "components/inputs";
import Toggle from "components/inputs/Toggle/Toggle";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useConstants } from "utils/hooks";
import { ControlledInput } from "components/Card/ControlledInput";
import type { CostProjectionDataType, MSADataType } from "types/DataTypes";

type AnnuityFundingProps = {
  total_medical: number;
  life_expectancy: number;
  big_ticket_item: number;
  recommended_initial_seed: number;
  annual_payment: number;
};

interface Props {
  service: MSADataType | CostProjectionDataType;
  onSave: (field: string, value: unknown) => void;
  reviewType: "MEDICAL" | "LEGAL";
}

const getServiceValues = (
  service: MSADataType | CostProjectionDataType,
  reviewType: "MEDICAL" | "LEGAL",
) => {
  const prefix = reviewType.toLowerCase() as "medical" | "legal";
  // both MSA and Cost Projection types have the same annuity funding fields
  return {
    total_medical: service[`${prefix}_total_proposed_amount`] || 0,
    life_expectancy: service.claimant_life_expectancy || 0,
    big_ticket_item: service[`${prefix}_annuity_big_ticket`] || 0,
    recommended_initial_seed: service[`${prefix}_seed_payment_amount`] || 0,
    annual_payment: service[`${prefix}_annual_payment_amount`] || 0,
  };
};

export const AnnuityFundingCard = ({ service, onSave, reviewType }: Props) => {
  const { constants } = useConstants();

  const PAYOUT_METHOD = useMemo(
    () => ({
      ANNUITY:
        constants?.PAYOUT_METHOD_CHOICES?.find(
          (c: { label: string; value: string | null }) => c.label === "Annuity",
        )?.value || "ANNUITY",
      LUMP_SUM:
        constants?.PAYOUT_METHOD_CHOICES?.find(
          (c: { label: string; value: string | null }) =>
            c.label === "Lump Sum",
        )?.value || "LUMP_SUM",
    }),
    [constants],
  );

  const isAnnuity = useMemo(
    () => service?.payout_method === PAYOUT_METHOD.ANNUITY,
    [service?.payout_method, PAYOUT_METHOD],
  );

  const totals = useMemo(
    () => getServiceValues(service, reviewType),
    [service, reviewType],
  );

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm<AnnuityFundingProps>({
    defaultValues: totals,
  });

  useEffect(() => {
    Object.entries(totals).forEach(([key, value]) => {
      setValue(key as keyof AnnuityFundingProps, value);
    });
  }, [setValue, totals]);

  return (
    <div className="data-block">
      <div className="data-block-header">Annuity Funding Breakdown</div>

      <div className="data-values wide-label-column">
        <div className="row">
          <label>Payment Method</label>
          <figure>
            <Toggle
              checked={isAnnuity}
              onChange={(checked) =>
                onSave(
                  "payout_method",
                  checked ? PAYOUT_METHOD.ANNUITY : PAYOUT_METHOD.LUMP_SUM,
                )
              }
              leftLabel="Lump Sum"
              rightLabel="Annuity"
            />
          </figure>
        </div>

        <div className="row">
          <label>Total Medical</label>
          <ControlledInput
            control={control}
            name="total_medical"
            errors={errors}
            onSave={async () => Promise.resolve()}
            readOnly={true}
            componentType={MaskedInput}
            mask="dollar"
          />
        </div>

        <div className="row">
          <label>LE</label>
          <ControlledInput
            control={control}
            name="life_expectancy"
            errors={errors}
            onSave={async () => Promise.resolve()}
            readOnly={true}
            componentType={MaskedInput}
            mask="age-decimal"
          />
        </div>

        <div className="row">
          <label>Big Ticket Item</label>
          <ControlledInput
            control={control}
            name="big_ticket_item"
            errors={errors}
            onSave={async () => Promise.resolve()}
            readOnly={true}
            componentType={MaskedInput}
            mask="dollar"
          />
        </div>

        <div className="row">
          <label>Recommended Initial Seed Amount</label>
          <ControlledInput
            control={control}
            name="recommended_initial_seed"
            errors={errors}
            onSave={async () => Promise.resolve()}
            readOnly={true}
            componentType={MaskedInput}
            mask="dollar"
          />
        </div>

        <div className="row">
          <label>Annual Payment Amounts</label>
          <ControlledInput
            control={control}
            name="annual_payment"
            errors={errors}
            onSave={async () => Promise.resolve()}
            readOnly={true}
            componentType={MaskedInput}
            mask="dollar"
          />
        </div>
      </div>
    </div>
  );
};

export default AnnuityFundingCard;
